import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cx } from '../../lib/util/cx';

/**
 * Port of the DS `Button` (components/actions/Button.jsx).
 *
 * Admin screens use `brown` (primary) and `ghost-dark` (secondary), almost
 * always at `sm`. Hover is a hue change brown → orange plus a 1px lift —
 * never a darken. Color-on-color variants use brown-deep text because white
 * on orange (3.35:1) and green (3.17:1) fails AA.
 */
export type ButtonVariant = 'brown' | 'orange' | 'green' | 'ghost-dark' | 'ghost-light';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  brown: 'bg-brown text-cream hover:bg-orange',
  orange: 'bg-orange text-brown-deep hover:bg-[#D65E20]',
  green: 'bg-green text-brown-deep hover:bg-[#3D7650]',
  'ghost-dark': 'bg-transparent text-brown-deep border-[1.5px] border-[rgba(58,30,15,.25)] hover:border-brown-deep',
  'ghost-light':
    'bg-transparent text-cream border-[1.5px] border-[rgba(241,237,225,.4)] hover:bg-[rgba(241,237,225,.08)] hover:border-cream',
};

const SIZE: Record<ButtonSize, string> = {
  xs: 'px-3 py-[5px] text-xs',
  sm: 'px-[22px] py-[11px] text-[15px]',
  md: 'px-[26px] py-4 text-base',
  lg: 'px-8 py-[18px] text-[17px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  /**
   * Set when the user may see this action but not take it (a missing write
   * permission): the button renders disabled — never hidden — with this text
   * as its tooltip.
   */
  lockedReason?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'brown',
    size = 'md',
    fullWidth = false,
    loading = false,
    lockedReason,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading || !!lockedReason;
  const button = (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cx(
        'items-center justify-center gap-2.5 rounded-pill font-body font-semibold leading-[1.2] no-underline',
        'transition-[transform,background-color,color,border-color] duration-fast',
        'enabled:hover:-translate-y-px enabled:active:opacity-80',
        'disabled:cursor-not-allowed disabled:opacity-50',
        fullWidth ? 'flex w-full' : 'inline-flex',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
  // A disabled button fires no mouse events, so the tooltip sits on a wrapper.
  return lockedReason ? (
    <span title={lockedReason} className={fullWidth ? 'flex w-full' : 'inline-flex'}>
      {button}
    </span>
  ) : (
    button
  );
});
